import React from 'react';
import styled from 'styled-components';
import { Text } from '@oneaudi/unified-web-components';

import { ConsumptionsAndEmissions } from '@oneaudi/feature-app-utils';
import type { UnifiedThemeType } from '@oneaudi/unified-web-common';
import { SYS_BREAKPOINT_MD, ThemeProvider } from '@oneaudi/unified-web-common';
import { Disclaimers } from './Disclaimers';
import type { AppearanceProps } from '../../../types';
import { ContentPosition, CTAsTheme } from '../../../types';
import { Context } from '../../Context';
import { getThemeName } from '../../utils/alphaHelper';

export const StageLegalWrapper = styled.div<{
  appearance: AppearanceProps;
  isModelPage?: boolean;
  isVideoStage?: boolean;
}>`
  width: auto;
  overflow: hidden;
  max-width: 1920px;
  position: relative;
  z-index: 2;
  pointer-events: auto;

  color: ${({ theme }) => {
    return theme.sem.color.ui.foreground.text.default;
  }};
  @media (min-width: ${() => SYS_BREAKPOINT_MD}) {
    ${({ appearance, isModelPage, isVideoStage }) => {
      if (
        appearance.backgroundDisclaimersCTAs === CTAsTheme.Media &&
        appearance.contentPosition !== ContentPosition.BOTTOM_LEFT &&
        !isModelPage
      ) {
        const bottom = isVideoStage ? 'var(--spacing-relative-3xl)' : '0';
        return `
        background: transparent;
        position: absolute;
        bottom: ${bottom};
        pointer-events: auto;
      `;
      }
      return `background: transparent;
      pointer-events: auto;
      padding: var(--spacing-relative-lg) var(--page-margin) var(--spacing-relative-xl)
      var(--page-margin);`;
    }}
  }
  ${({ isVideoStage }) => {
    const topPadding = isVideoStage ? 0 : 'var(--spacing-relative-md)';
    return `padding: ${topPadding} var(--page-margin) var(--spacing-relative-xl) var(--page-margin);`;
  }}
`;

interface StageLegalProps {
  disclaimers?: string[];
  appearance: AppearanceProps;
  isModelPage?: boolean;
  consumptionAndEmissions?: string[] | undefined;
  isVideoStage?: boolean;
}

const StageLegal: React.FC<StageLegalProps> = ({
  disclaimers,
  appearance,
  isModelPage,
  consumptionAndEmissions,
  isVideoStage,
}) => {
  const { localeService, oneGraphService, loggerService } = React.useContext(Context);
  // Checks if consumptionAndEmissions and disclaimers are not empty string arrays
  const hasContent = Boolean(
    (consumptionAndEmissions?.length &&
      consumptionAndEmissions.some((element) => element !== null)) ||
    disclaimers?.length,
  );
  const { backgroundDisclaimersCTAs, theme } = appearance;
  const fontSize = process.env.IS_LEGACY ? '2' : '3';

  if (!hasContent) return null;

  let caeTheme;
  if (isModelPage) {
    caeTheme = theme;
  } else if (backgroundDisclaimersCTAs === CTAsTheme.Media) {
    caeTheme = theme;
  } else {
    caeTheme = backgroundDisclaimersCTAs === CTAsTheme.Dark ? 'dark' : 'light';
  }

  caeTheme = getThemeName(caeTheme.toLowerCase() as UnifiedThemeType['name']);

  return (
    <ThemeProvider theme={{ name: caeTheme }}>
      <StageLegalWrapper
        appearance={appearance}
        isModelPage={isModelPage}
        isVideoStage={isVideoStage}
        data-testid="consumption-emissions-data"
      >
        {oneGraphService && localeService && (
          <Text variant={{ type: 'copy', order: fontSize, style: 'normal' }}>
            <ConsumptionsAndEmissions
              ids={consumptionAndEmissions}
              localeService={localeService}
              oneGraphService={oneGraphService}
              logger={loggerService}
            />
          </Text>
        )}

        <Disclaimers disclaimers={disclaimers} />
      </StageLegalWrapper>
    </ThemeProvider>
  );
};

export default StageLegal;
